/**
 * GAME FEEL — hitstop, screenshake, muzzle flash, and impact sparks.
 * Spec: bible 06-SHIPS-AND-WEAPONS §8.
 *
 * This is the layer that separates a good shmup from a technically-correct one.
 * None of it changes simulation outcomes; all of it changes how the simulation
 * *reads*. Two principles govern every number below:
 *
 *   1. FEEDBACK IS OVER-DELIVERED ON PURPOSE. The arcade original's explosions
 *      are disproportionately large and bright relative to what died. That is
 *      not a mistake — killing thirty things should feel like thirty
 *      accomplishments, not like erasing static.
 *   2. NOTHING OBSCURES A BULLET. Every effect renders behind the projectile
 *      layer and decays fast. An effect that hides incoming fire has traded
 *      feel for fairness, which is never the right trade.
 */

import * as THREE from 'three'

/** Tuning block. All durations in ms, all amplitudes in world units. */
export const FEEL = {
  /** Frame-freeze on a kill. Long enough to register, short enough to not stutter. */
  hitstop: { light: 28, armored: 55, structure: 90, bossPhase: 160 },
  /** Screenshake amplitude and decay. */
  shake: { light: 0.06, armored: 0.16, structure: 0.32, bomb: 0.55, playerDeath: 0.8, decay: 7.5 },
  /** Muzzle flash at the player's gun ports. */
  muzzle: { lifeMs: 60, scale: 0.42, sparkCount: 3, sparkSpeed: 14 },
  /** Sparks thrown off an enemy when hit but not killed. */
  spark: { lifeMs: 260, count: 5, speed: 11, spread: 1.2, size: 0.13 },
  /** Debris on a kill. */
  debris: { lifeMs: 620, count: 8, speed: 9, size: 0.19, gravity: -6 },
} as const

const MAX_PARTICLES = 900

/**
 * One pooled additive particle layer for every spark, muzzle flash, and debris
 * chunk in the game. Single instanced draw call, zero allocation per frame.
 */
export class GameFeel {
  readonly group = new THREE.Group()

  // Particle pool — struct-of-arrays so the update loop stays cache-friendly.
  private readonly px = new Float32Array(MAX_PARTICLES)
  private readonly py = new Float32Array(MAX_PARTICLES)
  private readonly pvx = new Float32Array(MAX_PARTICLES)
  private readonly pvy = new Float32Array(MAX_PARTICLES)
  private readonly plife = new Float32Array(MAX_PARTICLES)
  private readonly pmax = new Float32Array(MAX_PARTICLES)
  private readonly psize = new Float32Array(MAX_PARTICLES)
  private readonly pgrav = new Float32Array(MAX_PARTICLES)
  private readonly ptint = new Float32Array(MAX_PARTICLES * 3)
  private cursor = 0

  private readonly mesh: THREE.InstancedMesh
  private readonly dummy = new THREE.Object3D()
  private readonly color = new THREE.Color()

  /** Hitstop remaining, in seconds. The main loop scales dt by this. */
  private stopRemaining = 0
  /** Current shake amplitude; decays exponentially. */
  private shakeAmp = 0
  private shakeSeed = 0

  constructor() {
    this.group.name = 'GameFeelParticles'
    const geo = new THREE.PlaneGeometry(1, 1)
    const mat = new THREE.MeshBasicMaterial({
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
      opacity: 1,
    })
    this.mesh = new THREE.InstancedMesh(geo, mat, MAX_PARTICLES)
    this.mesh.frustumCulled = false
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(MAX_PARTICLES * 3),
      3,
    )
    // Render behind bullets — feel must never obscure a threat.
    this.mesh.renderOrder = 5
    this.group.add(this.mesh)
  }

  private spawn(
    x: number, y: number, vx: number, vy: number,
    lifeMs: number, size: number, r: number, g: number, b: number, grav = 0,
  ): void {
    const i = this.cursor
    this.cursor = (this.cursor + 1) % MAX_PARTICLES
    this.px[i] = x; this.py[i] = y
    this.pvx[i] = vx; this.pvy[i] = vy
    this.plife[i] = lifeMs / 1000
    this.pmax[i] = lifeMs / 1000
    this.psize[i] = size
    this.pgrav[i] = grav
    this.ptint[i * 3] = r; this.ptint[i * 3 + 1] = g; this.ptint[i * 3 + 2] = b
  }

  /**
   * Muzzle flash at a gun port. Fires every shot, so it must be cheap and
   * short — 60 ms, three sparks, no shake. At 9.5 shots/sec anything longer
   * becomes a continuous glow and stops reading as individual shots.
   */
  muzzleFlash(x: number, y: number): void {
    const cfg = FEEL.muzzle
    // Core flash — one bright short-lived quad.
    this.spawn(x, y, 0, 6, cfg.lifeMs, cfg.scale, 1.0, 0.92, 0.62)
    for (let i = 0; i < cfg.sparkCount; i++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 0.9
      const s = cfg.sparkSpeed * (0.6 + Math.random() * 0.7)
      this.spawn(x, y, Math.cos(a) * s, Math.sin(a) * s, cfg.lifeMs * 2.4, cfg.scale * 0.32, 1.0, 0.78, 0.35)
    }
  }

  /**
   * Sparks off armor — the enemy was hit but survived. This is the single most
   * important feedback in the game: without it the player cannot tell whether
   * their weapon is working against this target, which is exactly the question
   * the armor matchup table wants them asking.
   */
  hitSpark(x: number, y: number, effectiveness = 0.5): void {
    const cfg = FEEL.spark
    const count = Math.max(2, Math.round(cfg.count * (0.5 + effectiveness)))
    // Ineffective hits throw dull orange; effective hits throw hot white.
    const r = 1.0
    const g = 0.55 + effectiveness * 0.4
    const b = 0.2 + effectiveness * 0.65
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2
      const s = cfg.speed * (0.4 + Math.random())
      this.spawn(
        x, y,
        Math.cos(a) * s * cfg.spread, Math.sin(a) * s,
        cfg.lifeMs * (0.7 + Math.random() * 0.6),
        cfg.size, r, g, b, -3,
      )
    }
  }

  /** Kill burst. Scale and shake both key off armor class. */
  kill(x: number, y: number, tier: 'light' | 'armored' | 'structure' = 'light'): void {
    const cfg = FEEL.debris
    const scale = tier === 'light' ? 1 : tier === 'armored' ? 1.7 : 2.6
    const count = Math.round(cfg.count * scale)

    // Flash core — the "over-delivered" part.
    this.spawn(x, y, 0, 0, 120, 1.5 * scale, 1.0, 0.95, 0.8)
    this.spawn(x, y, 0, 0, 210, 2.4 * scale, 1.0, 0.62, 0.2)

    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2
      const s = cfg.speed * (0.5 + Math.random()) * scale
      this.spawn(
        x, y,
        Math.cos(a) * s, Math.sin(a) * s,
        cfg.lifeMs * (0.6 + Math.random() * 0.8),
        cfg.size * scale * (0.6 + Math.random() * 0.7),
        1.0, 0.6 + Math.random() * 0.3, 0.25, cfg.gravity,
      )
    }

    this.addShake(FEEL.shake[tier])
    this.addHitstop(FEEL.hitstop[tier])
  }

  addShake(amp: number): void {
    this.shakeAmp = Math.max(this.shakeAmp, amp)
  }

  addHitstop(ms: number): void {
    this.stopRemaining = Math.max(this.stopRemaining, ms / 1000)
  }

  /**
   * Consume hitstop for this frame.
   * Returns the dt the simulation should actually advance by. Hitstop freezes
   * the sim but NOT this particle layer — frozen sparks look like a bug.
   */
  consumeHitstop(dt: number): number {
    if (this.stopRemaining <= 0) return dt
    this.stopRemaining -= dt
    return this.stopRemaining > 0 ? 0 : dt
  }

  /** Camera offset for this frame. Deterministic per-seed, decays exponentially. */
  shakeOffset(out: THREE.Vector2): THREE.Vector2 {
    if (this.shakeAmp <= 0.0005) return out.set(0, 0)
    this.shakeSeed += 1
    const a = this.shakeSeed * 12.9898
    return out.set(
      (Math.sin(a) * 43758.5453 % 1) * this.shakeAmp,
      (Math.sin(a * 1.7) * 43758.5453 % 1) * this.shakeAmp,
    )
  }

  update(dt: number): void {
    this.shakeAmp *= Math.exp(-FEEL.shake.decay * dt)

    let visible = 0
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.plife[i] <= 0) continue
      this.plife[i] -= dt
      if (this.plife[i] <= 0) continue

      this.pvy[i] += this.pgrav[i] * dt
      this.px[i] += this.pvx[i] * dt
      this.py[i] += this.pvy[i] * dt

      const t = this.plife[i] / this.pmax[i]
      // Ease-out on scale, linear on brightness — reads as a spark cooling.
      const s = this.psize[i] * (0.25 + t * 0.75)
      this.dummy.position.set(this.px[i], this.py[i], 0.4)
      this.dummy.scale.set(s, s, 1)
      this.dummy.rotation.z = 0
      this.dummy.updateMatrix()
      this.mesh.setMatrixAt(visible, this.dummy.matrix)
      this.color.setRGB(this.ptint[i * 3] * t, this.ptint[i * 3 + 1] * t, this.ptint[i * 3 + 2] * t)
      this.mesh.setColorAt(visible, this.color)
      visible++
    }

    this.mesh.count = visible
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }

  reset(): void {
    this.plife.fill(0)
    this.shakeAmp = 0
    this.stopRemaining = 0
    this.mesh.count = 0
  }

  dispose(): void {
    this.mesh.geometry.dispose()
    ;(this.mesh.material as THREE.Material).dispose()
  }
}
